
import { KitSolar, EquipamentoSolar, DadosInstalacao } from "@/types";
import { SistemaBase } from "../SystemTypeSelector";

export const calcularPotenciaIdeal = (
  consumoMensal: number, 
  irradiacaoMedia: number,
  eficiencia: number = 0.85,
  perdas: number = 0.15
): number => {
  const fatorEficiencia = eficiencia * (1 - perdas);
  return Number((consumoMensal / (irradiacaoMedia * 30 * fatorEficiencia)).toFixed(2));
};

export const calcularCustoTelhado = (
  tipoTelhado: string | undefined,
  potenciaKwp: number,
  custosTelhado: Record<string, number> = {
    "ceramico": 200,
    "fibrocimento": 180,
    "metalico": 250,
    "laje": 150,
    "solo": 300
  }
): number => {
  if (!tipoTelhado || !custosTelhado[tipoTelhado]) {
    return 0;
  }
  return custosTelhado[tipoTelhado] * potenciaKwp;
};

export const gerarKitsSimulados = (
  potenciaIdeal: number, 
  configSistema: SistemaBase,
  tipoSistema: string,
  dadosIrradiacao: { irradiacaoMedia: number },
  dadosInstalacao?: DadosInstalacao
): KitSolar[] => {
  // Uso de painéis mais potentes (0.560 a 0.700 kWp)
  const marcasPaineis = ["Canadian Solar", "JinkoSolar", "Trina Solar", "JA Solar", "LONGi Solar"];
  const marcasInversores = ["Growatt", "Fronius", "Huawei", "SMA", "GoodWe"];
  const kits: KitSolar[] = [];
  
  const potencias = [
    Math.max(1, potenciaIdeal * 0.85),
    Math.max(1, potenciaIdeal * 0.95),
    potenciaIdeal,
    potenciaIdeal * 1.05,
    potenciaIdeal * 1.15
  ];
  
  const categorias = ["Econômico", "Standard", "Premium", "Profissional", "Elite"];
  const fornecedores = ["Edeltec Solar", "Solar Brasil", "Renovar Solar", "Eco Energia", "Sun Power"];
  
  const valorBaseKwp = configSistema.valorPorKwp;
  
  potencias.forEach((potencia, index) => {
    // Paineis mais potentes entre 560W e 700W
    const potenciasPaineis = [560, 590, 620, 650, 700];
    const potenciaPainel = potenciasPaineis[index] / 1000; // Convertendo para kWp
    const quantidadePaineis = Math.ceil(potencia / potenciaPainel);
    
    const marcaPainel = marcasPaineis[index];
    const marcaInversor = marcasInversores[index];
    
    const variacao = [0.85, 0.95, 1.0, 1.1, 1.2][index];
    let precoTotal = Math.round(potencia * valorBaseKwp * variacao);
    
    // Adicionar custo do telhado se disponível
    const custoTelhado = calcularCustoTelhado(dadosInstalacao?.tipoTelhado, potencia);
    precoTotal += custoTelhado;
    
    const precoPaineis = Math.round(precoTotal * 0.5);
    const precoInversor = Math.round(precoTotal * 0.3);
    const precoEstrutura = Math.round(precoTotal * 0.1);
    const precoOutros = Math.round(precoTotal * 0.1);
    
    const precoUnitarioPainel = Math.round(precoPaineis / quantidadePaineis);
    
    const geracaoEstimada = Math.round(
      potencia * dadosIrradiacao.irradiacaoMedia * 30 * configSistema.eficiencia * (1 - configSistema.perdas)
    );
    
    const tarifaMediaEnergia = 0.8;
    const economiaEstimada = geracaoEstimada * tarifaMediaEnergia;
    const paybackEstimado = Math.round(precoTotal / economiaEstimada);
    
    const kitFornecedor = fornecedores[index];
    
    kits.push({
      id: `kit-${tipoSistema}-${index + 1}`,
      nome: `${configSistema.nome} ${categorias[index]} - ${potencia.toFixed(2)}kWp`,
      potenciaSistema: potencia,
      paineis: [{
        id: `painel-${index + 1}`,
        tipo: "painel",
        marca: marcaPainel,
        modelo: `${marcaPainel} ${potenciasPaineis[index]}W`,
        potencia: potenciaPainel,
        quantidade: quantidadePaineis,
        precoUnitario: precoUnitarioPainel,
        fornecedor: kitFornecedor,
        disponibilidade: true,
        eficiencia: 19 + (index * 1),
        garantia: 25
      }],
      inversores: [{
        id: `inversor-${index + 1}`,
        tipo: "inversor",
        marca: marcaInversor,
        modelo: `${marcaInversor} ${Math.ceil(potencia)}kW`,
        potencia: Math.ceil(potencia),
        quantidade: 1,
        precoUnitario: precoInversor,
        fornecedor: kitFornecedor,
        disponibilidade: true,
        garantia: 5 + (index * 1)
      }],
      estruturas: [{
        id: `estrutura-${index + 1}`,
        tipo: "estrutura",
        marca: "Romagnole",
        modelo: "Estrutura Padrão",
        quantidade: 1,
        precoUnitario: precoEstrutura,
        fornecedor: kitFornecedor,
        disponibilidade: true
      }],
      outrosEquipamentos: [{
        id: `outros-${index + 1}`,
        tipo: "outros",
        marca: "Diversos",
        modelo: "Kit Cabeamento e Proteções",
        quantidade: 1,
        precoUnitario: precoOutros,
        fornecedor: kitFornecedor,
        disponibilidade: true
      }],
      precoTotal,
      custoTelhado,
      geracaoEstimada,
      paybackEstimado,
      fornecedor: kitFornecedor
    });
  });
  
  return kits;
};

export const getSistemaPadraoConfig = (): Record<string, SistemaBase> => {
  return {
    tradicional: {
      nome: "Sistema Tradicional",
      descricao: "Sistema fotovoltaico com inversor string tradicional",
      valorPorKwp: 3800,
      eficiencia: 0.85,
      perdas: 0.15,
      fatorSimultaneidade: 0.9,
      fatorCapacidade: 0.18
    },
    mlpe: {
      nome: "Sistema MLPE",
      descricao: "Sistema com otimizadores ou microinversores",
      valorPorKwp: 4500,
      eficiencia: 0.88,
      perdas: 0.12,
      fatorSimultaneidade: 0.92,
      fatorCapacidade: 0.2
    },
    hibrido: {
      nome: "Sistema Híbrido",
      descricao: "Sistema fotovoltaico com banco de baterias para backup",
      valorPorKwp: 5200,
      eficiencia: 0.83,
      perdas: 0.17,
      fatorSimultaneidade: 0.95,
      fatorCapacidade: 0.19
    },
    offgrid: {
      nome: "Sistema Off-Grid",
      descricao: "Sistema fotovoltaico autônomo sem conexão à rede",
      valorPorKwp: 6500,
      eficiencia: 0.8,
      perdas: 0.2,
      fatorSimultaneidade: 0.98,
      fatorCapacidade: 0.21
    }
  };
};
